import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUser } from '@/constants/UserContext';
import { savePostAuthRedirect } from '@/utils/postAuthRedirect';
import { logger } from '@/utils/logger';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Skip the onboarding gate (only the onboarding page itself sets this). */
  bypassOnboarding?: boolean;
}

export function ProtectedRoute({ children, bypassOnboarding = false }: ProtectedRouteProps) {
  const { session, loading, authStatus } = useAuth();
  const { user, profileLoading } = useUser();
  const location = useLocation();

  const authPending = loading || authStatus === 'loading';

  if (authPending || (session?.user?.id && profileLoading)) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-white)' }}>
        <p style={{ fontFamily: 'Inter', color: 'var(--color-primary)' }}>Loading...</p>
      </div>
    );
  }

  // Require a real Supabase user id — not just truthy session (partial/stale client state).
  if (authStatus === 'unauthenticated' || !session?.user?.id) {
    savePostAuthRedirect(`${location.pathname}${location.search}`);
    logger.info('[ProtectedRoute] Redirecting to login', {
      pathname: location.pathname,
      authStatus,
      hasSessionUser: Boolean(session?.user?.id),
      profileLoading,
    });
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Wait until profile row is loaded before deciding onboarding state — UserContext
  // populates `user` after auth resolves; treating undefined as "needs onboarding"
  // would briefly redirect even returning users on cold start.
  if (!bypassOnboarding && user && user.onboarding_completed === false) {
    savePostAuthRedirect(`${location.pathname}${location.search}`);
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}
